import {
    adjustSiteStock,
    STOCK_ADJUSTMENT_REASONS,
    type AdjustStockRequest,
} from '@api/cm-sites';
import type { BucketedBatchLine } from '@api/vendor-portal';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Modal, SegmentedControl, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { readApiError } from '@utils/apiError';
import { type FC, useEffect, useMemo, useState } from 'react';

/**
 * What is being corrected, in the least each caller can supply.
 *
 * Structural rather than BucketedItemBalance, because the two drawers hold different rows: the
 * stock drawer has the item and its batches, the batch register has a batch that knows its
 * item. Both can answer this much, and neither has to be reshaped into the other.
 */
export interface AdjustTarget {
    itemId: string;
    itemName?: string | null;
    itemType: string;
    /** Offered in the picker. Empty when the caller already pinned a batch. */
    batches?: BucketedBatchLine[];
}

interface AdjustStockModalProps {
    warehouseId: string;
    /** The item whose figure is being corrected. Null closes the modal. */
    item: AdjustTarget | null;
    /**
     * Pinned when the correction was opened from a batch row rather than an item.
     *
     * The batch is then fixed rather than picked — somebody looking at one batch's number is
     * correcting that batch, and re-asking which one invites the wrong answer.
     */
    batch?: BucketedBatchLine | null;
    /**
     * Whether this brand traces this kind of item by batch.
     *
     * Decides only whether the field is REQUIRED. It is offered either way, because a batch
     * somebody knows is worth recording even where the brand does not demand one — the same
     * rule the receive path follows.
     */
    batchRequired: boolean;
    onClose: () => void;
    onAdjusted: () => void;
}

/**
 * An ad-hoc correction to what a site is holding.
 *
 * THE ONLY MOVEMENT WITH NO DOCUMENT BEHIND IT. A delivery has a challan and a run has an
 * output; this has somebody's word that the number is wrong, which is why the reason is asked
 * rather than offered and why direction is stated rather than inferred from a minus sign.
 */
const AdjustStockModal: FC<AdjustStockModalProps> = ({
    warehouseId,
    item,
    batch,
    batchRequired,
    onClose,
    onAdjusted,
}) => {
    const { showToastSuccess, showToastError } = useToaster();

    const [direction, setDirection] = useState<'more' | 'less'>('more');
    const [quantity, setQuantity] = useState('');
    const [reasonCode, setReasonCode] = useState<string | null>(null);
    const [batchId, setBatchId] = useState<string | null>(null);
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const opened = !!item;

    useEffect(() => {
        if (!opened) return;
        setDirection('more');
        setQuantity('');
        setReasonCode(null);
        setBatchId(batch?.batchId || null);
        setNotes('');
    }, [opened, batch?.batchId]);

    const batchOptions = useMemo(
        () =>
            (item?.batches ?? [])
                // The empty string is the no-batch bucket, not a batch somebody can name.
                .filter((line) => line.batchId)
                .map((line) => ({
                    value: line.batchId,
                    label: `${line.batchCode ?? line.batchId} · ${line.quantity} on hand`,
                })),
        [item],
    );

    const amount = Number(quantity);
    const canSubmit =
        Number.isFinite(amount) &&
        amount > 0 &&
        !!reasonCode &&
        (!batchRequired || !!batchId) &&
        !isSubmitting;

    const handleSubmit = async () => {
        if (!item || !reasonCode) return;

        setIsSubmitting(true);
        try {
            const request: AdjustStockRequest = {
                warehouseId,
                itemId: item.itemId,
                itemType: item.itemType === 'product' ? 'product' : 'material',
                quantity: amount,
                incoming: direction === 'more',
                reasonCode,
                batchId: batchId || undefined,
                notes: notes.trim() || undefined,
            };

            await adjustSiteStock(request);

            showToastSuccess({
                title: `${item.itemName ?? item.itemId} corrected`,
                subtitle: `${direction === 'more' ? 'Added' : 'Removed'} ${amount}. The change is on the site's ledger with its reason.`,
            });
            onAdjusted();
            onClose();
        } catch (error) {
            showToastError({
                title: 'Not recorded',
                subtitle: readApiError(error, 'The correction could not be saved.'),
            });
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="md"
            centered
            title={
                <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                    Adjust {item?.itemName ?? item?.itemId}
                </Text>
            }
        >
            <Stack gap={16}>
                <Text fz={13} c={neutralColors.textSecondary}>
                    For a count that disagrees with the book. This writes to the site&apos;s
                    ledger with its reason, and can be undone from History.
                </Text>

                <SegmentedControl
                    value={direction}
                    onChange={(value) => setDirection(value as 'more' | 'less')}
                    data={[
                        { value: 'more', label: 'Count found more' },
                        { value: 'less', label: 'Count found less' },
                    ]}
                />

                {/* Bottom-aligned, so the two boxes line up whatever sits above each. */}
                <Group grow align="flex-end">
                    <TextInput
                        label="How many"
                        description="The difference, not the new total"
                        placeholder="0"
                        inputMode="numeric"
                        value={quantity}
                        onChange={(event) => setQuantity(event.currentTarget.value)}
                        withAsterisk
                        data-autofocus
                    />
                    <Select
                        label="Why"
                        placeholder="Pick a reason"
                        data={[...STOCK_ADJUSTMENT_REASONS]}
                        value={reasonCode}
                        onChange={setReasonCode}
                        withAsterisk
                    />
                </Group>

                {batch ? (
                    <Text fz={13} c={neutralColors.textSecondary}>
                        Against batch <strong>{batch.batchCode ?? batch.batchId}</strong>, which
                        holds {batch.quantity}.
                    </Text>
                ) : (
                    // Nothing to pick and nothing demanded, so the field could only ever be blank.
                    (batchRequired || batchOptions.length > 0) && (
                    <Select
                        label="Which batch"
                        description={
                            batchRequired
                                ? 'This brand traces this item by batch'
                                : 'Optional — name one if you know it'
                        }
                        placeholder={batchRequired ? 'Pick the batch' : 'No particular batch'}
                        data={batchOptions}
                        value={batchId}
                        onChange={setBatchId}
                        withAsterisk={batchRequired}
                        searchable
                        clearable={!batchRequired}
                    />
                    )
                )}

                <Textarea
                    label="Note"
                    placeholder="Anything worth saying about this count"
                    value={notes}
                    onChange={(event) => setNotes(event.currentTarget.value)}
                    minRows={2}
                    autosize
                />

                {direction === 'less' && (
                    <Text fz={12} c={semanticColors.warning.dark}>
                        A removal cannot take more than the batch holds — the site&apos;s balance
                        is the ceiling.
                    </Text>
                )}

                <Group justify="flex-end" gap={8}>
                    <Button variant={ButtonVariant.SUBTLE} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant={ButtonVariant.ACTION}
                        disabled={!canSubmit}
                        loading={isSubmitting}
                        onClick={handleSubmit}
                    >
                        Record adjustment
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AdjustStockModal;
